/* Bettr Sauna — Custom Element registry
 * --------------------------------------------------------------------
 * Phase 13: Custom Element wrappers over existing components. No
 * Shadow DOM — merchants must be able to edit component markup from
 * the Shopify Theme Editor, and shadow trees hide children from the
 * section-settings inspector.
 *
 * Every wrapper defaults to `display: contents` (handled in the
 * stylesheet below) so the CE adds zero layout. Existing CSS selectors
 * targeting the inner markup continue to match.
 *
 * What CEs provide over the plain IIFE+section:load pattern:
 *   - A stable registry: customElements.get('bettr-cart-drawer') works
 *     from anywhere, no brittle DOM probing.
 *   - Explicit connectedCallback/disconnectedCallback — Theme Editor
 *     section reloads trigger these automatically, no separate
 *     shopify:section:unload hook.
 *   - attributeChangedCallback for reactive attribute surfaces
 *     (data-selected-variant-id, data-product-handle, etc.).
 *   - Future-ready: declarative Shadow DOM attachment is a one-line
 *     change per class if/when that becomes desirable.
 *
 * The `<bettr-qty-input>` below is a real form-associated custom
 * element — it participates in <form> submission like a native input,
 * which sets up Phase 16 (Form Conversion) nicely.
 * -------------------------------------------------------------------- */

(function () {
  if (!('customElements' in window)) return;

  // ------------------------------------------------------------------
  // Base mix-in: lifecycle hooks fire custom events + track mount.
  // ------------------------------------------------------------------
  class BettrBaseElement extends HTMLElement {
    constructor() {
      super();
      this._mounted = false;
    }
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      if (typeof this.onMount === 'function') this.onMount();
      this.dispatchEvent(new CustomEvent('bettr:mount', { bubbles: false }));
    }
    disconnectedCallback() {
      if (!this._mounted) return;
      this._mounted = false;
      if (typeof this.onUnmount === 'function') this.onUnmount();
      this.dispatchEvent(new CustomEvent('bettr:unmount', { bubbles: false }));
    }
  }

  // ------------------------------------------------------------------
  // <bettr-cart-drawer> — thin wrapper. Cart behavior lives in
  // assets/custom-cart.js (deferred from theme.liquid). This CE provides a
  // stable querySelector target and a reliable unmount hook for Theme Editor
  // target and a reliable unmount hook for Theme Editor section reloads.
  // ------------------------------------------------------------------
  class BettrCartDrawer extends BettrBaseElement {
    get isOpen() {
      const section = this.querySelector('.cart-drawer-section');
      return !!section && section.getAttribute('aria-hidden') === 'false';
    }
  }

  // ------------------------------------------------------------------
  // <bettr-compare-modal>
  // ------------------------------------------------------------------
  class BettrCompareModal extends BettrBaseElement {
    open() {
      if (typeof window.openCompareModal === 'function') window.openCompareModal();
    }
  }

  // ------------------------------------------------------------------
  // <bettr-addon-modal>
  // ------------------------------------------------------------------
  class BettrAddonModal extends BettrBaseElement {}

  // ------------------------------------------------------------------
  // <bettr-variant-selector> — reactive variant attribute.
  // Consumers watch for `bettr:variant-change`; PDP JS dispatches.
  // ------------------------------------------------------------------
  class BettrVariantSelector extends BettrBaseElement {
    static get observedAttributes() {
      return ['data-selected-variant-id'];
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (!this._mounted) return;
      if (name === 'data-selected-variant-id' && oldValue !== newValue) {
        this.dispatchEvent(new CustomEvent('bettr:variant-change', {
          detail: { variantId: newValue },
          bubbles: true,
        }));
      }
    }
  }

  // ------------------------------------------------------------------
  // <bettr-lineup-card> — exposes a robust productHandle getter that
  // fixes the Phase 4d fallback fragility:
  //   1. data-product-handle attribute (explicit override, highest priority)
  //   2. first internal /products/<handle> link in the card
  //   3. '' (caller decides)
  //
  // View Transition names are still emitted server-side from Liquid;
  // this getter lets JS consumers (e.g. Phase 21 analytics) resolve the
  // handle cheaply without re-parsing HTML.
  // ------------------------------------------------------------------
  class BettrLineupCard extends BettrBaseElement {
    get productHandle() {
      const explicit = this.getAttribute('data-product-handle');
      if (explicit) return explicit;
      const a = this.querySelector('a[href*="/products/"]');
      if (!a) return '';
      const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      return m ? m[1] : '';
    }
  }

  // ------------------------------------------------------------------
  // <bettr-qty-input> — form-associated custom element.
  // Markup:
  //   <bettr-qty-input name="updates[123]" value="1" min="1" max="9">
  //     <button data-qty-decr aria-label="Decrease">−</button>
  //     <input data-qty-text type="text" inputmode="numeric" value="1">
  //     <button data-qty-incr aria-label="Increase">+</button>
  //   </bettr-qty-input>
  //
  // Participates in <form> submission natively — FormData(form).get('updates[123]')
  // returns the current value. Dispatches `bettr:qty-change` on value
  // change so consumers (optimistic cart UI) can react.
  // ------------------------------------------------------------------
  class BettrQtyInput extends BettrBaseElement {
    static get formAssociated() { return true; }
    static get observedAttributes() { return ['value', 'min', 'max', 'disabled']; }

    constructor() {
      super();
      if ('attachInternals' in this) {
        this._internals = this.attachInternals();
      }
    }

    onMount() {
      this._decr = this.querySelector('[data-qty-decr]');
      this._incr = this.querySelector('[data-qty-incr]');
      this._text = this.querySelector('[data-qty-text]');
      this._onDecr = () => this._step(-1);
      this._onIncr = () => this._step(1);
      this._onInput = (e) => this._setValue(e.target.value, false);
      this._onBlur = (e) => this._setValue(e.target.value, true);
      if (this._decr) this._decr.addEventListener('click', this._onDecr);
      if (this._incr) this._incr.addEventListener('click', this._onIncr);
      if (this._text) {
        this._text.addEventListener('input', this._onInput);
        this._text.addEventListener('blur', this._onBlur);
      }
      this._syncFormValue();
    }

    onUnmount() {
      if (this._decr) this._decr.removeEventListener('click', this._onDecr);
      if (this._incr) this._incr.removeEventListener('click', this._onIncr);
      if (this._text) {
        this._text.removeEventListener('input', this._onInput);
        this._text.removeEventListener('blur', this._onBlur);
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'value' && this._text && this._text.value !== newValue) {
        this._text.value = newValue;
        this._syncFormValue();
      }
    }

    get value() {
      return this.getAttribute('value') || (this._text && this._text.value) || '';
    }

    set value(v) {
      this.setAttribute('value', String(v));
    }

    _step(delta) {
      const cur = parseInt(this.value, 10) || 0;
      this._setValue(cur + delta, true);
    }

    _setValue(raw, commit) {
      let n = parseInt(raw, 10);
      if (isNaN(n)) n = parseInt(this.getAttribute('min') || '0', 10);
      const min = parseInt(this.getAttribute('min') || '0', 10);
      const max = parseInt(this.getAttribute('max') || '999', 10);
      if (n < min) n = min;
      if (n > max) n = max;
      if (this._text) this._text.value = n;
      if (commit) {
        this.setAttribute('value', String(n));
        this._syncFormValue();
        this.dispatchEvent(new CustomEvent('bettr:qty-change', {
          detail: { value: n }, bubbles: true,
        }));
      }
    }

    _syncFormValue() {
      if (this._internals && typeof this._internals.setFormValue === 'function') {
        this._internals.setFormValue(this.value);
      }
    }
  }

  // ------------------------------------------------------------------
  // <bettr-share-button> — Phase 25 Web Share API integration.
  // Usage:
  //   <bettr-share-button data-title="Ritual 2" data-url="/products/ritual-2">
  //     <button>Share</button>
  //   </bettr-share-button>
  //
  // Uses navigator.share when available (mobile + macOS Safari), falls
  // back to clipboard write + a toast. Hides its slotted content when
  // neither is supported so non-capable browsers don't see a dead button.
  // ------------------------------------------------------------------
  class BettrShareButton extends BettrBaseElement {
    onMount() {
      this._hasShare = typeof navigator.share === 'function';
      this._hasClipboard = !!(navigator.clipboard && navigator.clipboard.writeText);
      if (!this._hasShare && !this._hasClipboard) {
        this.style.display = 'none';
        return;
      }
      this._onClick = (e) => this._trigger(e);
      this.addEventListener('click', this._onClick);
    }
    onUnmount() {
      if (this._onClick) this.removeEventListener('click', this._onClick);
    }
    async _trigger(e) {
      const btn = e.target && e.target.closest('button, a');
      if (!btn) return;
      e.preventDefault();
      const url = new URL(this.getAttribute('data-url') || location.pathname, location.origin).toString();
      const title = this.getAttribute('data-title') || document.title;
      const text = this.getAttribute('data-text') || '';
      try {
        if (this._hasShare) {
          await navigator.share({ title, text, url });
          this.dispatchEvent(new CustomEvent('bettr:share', { detail: { method: 'share' }, bubbles: true }));
          return;
        }
        if (this._hasClipboard) {
          await navigator.clipboard.writeText(url);
          this._toast('Link copied');
          this.dispatchEvent(new CustomEvent('bettr:share', { detail: { method: 'clipboard' }, bubbles: true }));
        }
      } catch (err) { /* user cancelled — silent */ }
    }
    _toast(msg) {
      const t = document.createElement('div');
      t.textContent = msg;
      t.setAttribute('role', 'status');
      t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#0C0F09;color:#FFF;padding:10px 18px;border-radius:999px;font-size:14px;z-index:10001;opacity:0;transition:opacity .2s ease-out;pointer-events:none';
      document.body.appendChild(t);
      requestAnimationFrame(() => { t.style.opacity = '1'; });
      setTimeout(() => {
        t.style.opacity = '0';
        setTimeout(() => t.remove(), 220);
      }, 1600);
    }
  }

  // ------------------------------------------------------------------
  // Register all.
  // ------------------------------------------------------------------
  const registry = [
    ['bettr-cart-drawer', BettrCartDrawer],
    ['bettr-compare-modal', BettrCompareModal],
    ['bettr-addon-modal', BettrAddonModal],
    ['bettr-variant-selector', BettrVariantSelector],
    ['bettr-lineup-card', BettrLineupCard],
    ['bettr-qty-input', BettrQtyInput],
    ['bettr-share-button', BettrShareButton],
  ];
  registry.forEach(([name, ctor]) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  });
})();
